import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import { TypeReport } from '@/core/domain/NetReport/Financial/Financial.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Col, Row, Form } from 'antd';
import moment, { Moment } from 'moment';
import { useIntl } from 'react-intl';

export default function FinancialCashFlowDateForm() {
  const intl = useIntl();
  const form = Form.useFormInstance();
  const typeReport = Form.useWatch('typeReport', form);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  function validationDates(value: Moment) {
    if (!value) {
      return Promise.resolve();
    }

    const currentStartDate = form.getFieldValue('startDate');
    const currentEndDate = form.getFieldValue('endDate');

    if (currentStartDate && currentEndDate) {
      if (currentStartDate.isAfter(currentEndDate)) {
        return Promise.reject(
          `A data de início deve ser anterior ou igual à data de término.`,
        );
      }

      const maxEndDate = moment(currentStartDate)
        .add(1, 'year')
        .subtract(1, 'day');

      if (currentEndDate.isAfter(maxEndDate)) {
        return Promise.reject(
          `O intervalo entre as datas de filtro é de até 12 meses`,
        );
      }
    }

    return Promise.resolve();
  }

  const isDaily = typeReport === TypeReport.daily;

  return (
    <>
      <Row gutter={16}>
        <Col span={8}>
          <FloatingFormItem
            label={intl.formatMessage({ id: 'reports.form.in' })}
            name="startDate"
            initialValue={moment().startOf('month')}
            rules={
              isDaily
                ? [
                    {
                      validator: (_, value) => validationDates(value),
                    },
                  ]
                : [defaultEmptyFieldError]
            }
          >
            <InputDate
              disabledDate={(date) => {
                const currentEndDate = form.getFieldValue('endDate');
                if (!currentEndDate) return false;

                const minStartDate = moment(currentEndDate)
                  .subtract(1, 'year')
                  .add(1, 'day');
                const maxStartDate = moment(currentEndDate)
                  .add(1, 'year')
                  .add(1, 'day');

                return (
                  date.isAfter(currentEndDate) ||
                  date.isBefore(minStartDate) ||
                  date.isAfter(maxStartDate)
                );
              }}
            />
          </FloatingFormItem>
        </Col>

        {isDaily && (
          <Col span={8}>
            <FloatingFormItem
              label={intl.formatMessage({ id: 'reports.form.from' })}
              name="endDate"
              initialValue={moment().startOf('month')}
              rules={
                isDaily
                  ? [
                      {
                        validator: (_, value) => validationDates(value),
                      },
                    ]
                  : undefined
              }
            >
              <InputDate
                disabledDate={(date) => {
                  const currentStartDate = form.getFieldValue('startDate');
                  if (!currentStartDate) return false;

                  const maxEndDate = moment(currentStartDate)
                    .add(1, 'year')
                    .subtract(1, 'day');

                  return (
                    date.isBefore(currentStartDate) || date.isAfter(maxEndDate)
                  );
                }}
              />
            </FloatingFormItem>
          </Col>
        )}
      </Row>

      <Row gutter={16}>
        <Col span={16}>
          <FloatingFormItem
            name="companiesIds"
            label={intl.formatMessage({ id: 'reports.form.companies' })}
          >
            <CompaniesInfiniteSelectField
              allowClear
              mode="multiple"
              style={{ width: '100%' }}
              queryFilter={{ rows: 50 }}
              queryEnable
              selectAll
              activityFilter
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </>
  );
}
